import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail } from 'lucide-react';

const LAST_UPDATED = '2026-07-26';

const IntegritetspolicyPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }} data-testid="integritetspolicy-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#F7F4EF]/90 backdrop-blur-md border-b border-[#E8E2D9]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="back-home-link">
            <img src="/operio-logo-2.png" alt="Operio" className="h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#6B6560]">
            <Link to="/" className="hover:text-[#1A1A1A] transition-colors">Hem</Link>
            <Link to="/about" className="hover:text-[#1A1A1A] transition-colors">Om oss</Link>
            <Link to="/kontakt" className="hover:text-[#1A1A1A] transition-colors">Kontakt</Link>
          </nav>
        </div>
      </header>

      <main className="pt-32 pb-20 max-w-4xl mx-auto px-6">
        {/* Hero */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-white border border-[#E8E2D9] rounded-full px-4 py-1.5 mb-6">
            <ShieldCheck size={14} className="text-[#E8613A]" />
            <span className="text-xs uppercase tracking-widest text-[#6B6560] font-semibold">Integritetspolicy</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight mb-4"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Din data är din.
          </h1>
          <p className="text-lg text-[#6B6560]">
            Så här hanterar Operio dina personuppgifter — enligt GDPR och svensk lag.
          </p>
          <p className="text-xs text-[#6B6560] mt-3">Senast uppdaterad: {LAST_UPDATED}</p>
        </div>

        {/* Content */}
        <article className="prose prose-neutral max-w-none space-y-8" data-testid="integritetspolicy-content">
          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              1. Personuppgiftsansvarig
            </h2>
            <p className="text-[#3A3A3A] leading-relaxed">
              Operio AB (org.nr under registrering), Skövde, Sverige, är personuppgiftsansvarig
              för de personuppgifter som samlas in via tjänsten Operio (webbapp och mobilapp).
              Kontakt: <a href="mailto:hej@operio.nu" className="text-[#E8613A] font-medium hover:underline">hej@operio.nu</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              2. Vilka uppgifter samlar vi in?
            </h2>
            <ul className="space-y-2 text-[#3A3A3A]">
              <li><strong>Kontouppgifter:</strong> namn, e-post, telefonnummer, företagsnamn, organisationsnummer.</li>
              <li><strong>Användardata:</strong> tidrapporter, projekt, maskinlogg, ÄTA-signeringar, dagboksanteckningar och fakturor du skapar i tjänsten.</li>
              <li><strong>Betalningsdata:</strong> hanteras av Stripe (PCI-DSS-certifierad). Operio ser aldrig kortnummer.</li>
              <li><strong>Bokföringsdata:</strong> om du importerar SIE4/CSV/Excel från Fortnox/Bokio lagras dessa krypterat på våra servrar i EU.</li>
              <li><strong>Enhetsdata (mobilappen):</strong> enhets-ID, push-token (FCM), operativsystemversion — endast för att kunna skicka aviseringar.</li>
              <li><strong>Platsdata:</strong> vid inklockning på arbetsplats sparar vi (om du godkänner) GPS-koordinater. Kan stängas av i inställningarna.</li>
              <li><strong>Automatiskt insamlade uppgifter:</strong> IP-adress, webbläsartyp, sessionstider (via 1st-party cookies för inloggning).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              3. Rättslig grund
            </h2>
            <p className="text-[#3A3A3A] leading-relaxed">
              Vi behandlar dina uppgifter baserat på (a) <strong>avtal</strong> — för att kunna
              leverera tjänsten du prenumererar på, (b) <strong>samtycke</strong> — för
              marknadsföringsmejl och GPS-loggning, samt (c) <strong>rättslig förpliktelse</strong> —
              för bokförings- och skattelagstiftning.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              4. Vad använder vi datan till?
            </h2>
            <ul className="space-y-2 text-[#3A3A3A]">
              <li>Leverera funktioner (projektstyrning, tidrapportering, fakturering, AI-prognoser).</li>
              <li>Skicka aviseringar om nya tidrapporter, ÄTA-signeringar, fakturor, m.m.</li>
              <li>Analysera och förbättra tjänsten (aggregerad, anonymiserad statistik).</li>
              <li>Kundsupport och kommunikation.</li>
              <li>Uppfylla lagkrav (bokföring 7 år enligt Bokföringslagen).</li>
            </ul>
            <p className="text-[#3A3A3A] leading-relaxed mt-3">
              <strong>Vi säljer aldrig din data.</strong> Vi använder den aldrig för profilering
              utanför tjänsten. Vi tränar aldrig externa AI-modeller på din data — de LLM-anrop
              vi gör (OpenAI, Google Gemini) skickar endast anonymiserad kontext utan
              personidentifierande innehåll.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              5. Delning med tredje part
            </h2>
            <p className="text-[#3A3A3A] leading-relaxed mb-2">
              Vi delar endast data med följande underbiträden, alla GDPR-kompatibla:
            </p>
            <ul className="space-y-1 text-[#3A3A3A]">
              <li><strong>Stripe</strong> (betalning, EU/US via SCC)</li>
              <li><strong>Resend</strong> (transaktionella e-postutskick, EU)</li>
              <li><strong>Firebase / Google Cloud Messaging</strong> (push-notiser, EU/US via SCC)</li>
              <li><strong>OpenAI / Google (Gemini)</strong> (LLM-anrop för AI-funktioner, kontextuell data utan personuppgifter)</li>
              <li><strong>IDURA / BankID</strong> (digital signering, endast Sverige)</li>
              <li><strong>MongoDB Atlas</strong> (datalagring, EU-region Frankfurt)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              6. Lagringstid
            </h2>
            <ul className="space-y-2 text-[#3A3A3A]">
              <li>Kontodata: så länge kontot är aktivt + 90 dagar därefter.</li>
              <li>Fakturor och bokföringsdata: 7 år (Bokföringslagen).</li>
              <li>Loggar och sessionsdata: 12 månader.</li>
              <li>Push-tokens: raderas automatiskt 30 dagar efter inaktivitet.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              7. Dina rättigheter (GDPR)
            </h2>
            <ul className="space-y-2 text-[#3A3A3A]">
              <li><strong>Rätt till information:</strong> se den här sidan.</li>
              <li><strong>Rätt till tillgång:</strong> begär en kopia av dina uppgifter.</li>
              <li><strong>Rätt till rättelse:</strong> ändra felaktiga uppgifter (via kontoinställningar eller mejl).</li>
              <li><strong>Rätt till radering:</strong> begär att kontot raderas. Fakturadata måste sparas enligt lag.</li>
              <li><strong>Rätt till dataportabilitet:</strong> vi exporterar all din data som JSON på begäran.</li>
              <li><strong>Rätt att invända:</strong> mot behandling baserad på berättigat intresse.</li>
              <li><strong>Rätt att klaga:</strong> till <a href="https://www.imy.se" target="_blank" rel="noreferrer" className="text-[#E8613A] hover:underline">Integritetsskyddsmyndigheten (IMY)</a>.</li>
            </ul>
            <p className="text-[#3A3A3A] leading-relaxed mt-3">
              Utöva dina rättigheter genom att mejla{' '}
              <a href="mailto:hej@operio.nu" className="text-[#E8613A] font-medium hover:underline">hej@operio.nu</a>.
              Vi svarar inom 30 dagar.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              8. Cookies
            </h2>
            <p className="text-[#3A3A3A] leading-relaxed">
              Vi använder endast <strong>nödvändiga cookies</strong> (inloggningssession).
              Inga marknadsföringscookies. Inga tredjeparts-trackers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              9. Säkerhet
            </h2>
            <p className="text-[#3A3A3A] leading-relaxed">
              All trafik krypteras via TLS 1.3. Lösenord hashas med bcrypt. Backups tas dagligen
              och lagras krypterat i EU. Alla anställda med databasåtkomst har genomgått
              säkerhetsutbildning och signerat sekretessavtal.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              10. Barn
            </h2>
            <p className="text-[#3A3A3A] leading-relaxed">
              Operio är en B2B-tjänst och riktar sig inte till personer under 18 år. Vi samlar
              inte medvetet in data om minderåriga.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              11. Ändringar av policyn
            </h2>
            <p className="text-[#3A3A3A] leading-relaxed">
              Vid väsentliga ändringar meddelar vi dig via e-post minst 30 dagar i förväg.
              Uppdaterade versioner finns alltid på{' '}
              <span className="font-mono text-sm">operio.nu/integritetspolicy</span>.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-white border border-[#E8E2D9] rounded-2xl p-6 mt-10" data-testid="policy-contact-box">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#E8613A]/10 rounded-xl">
                <Mail size={20} className="text-[#E8613A]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-1"
                    style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Frågor om din data?
                </h3>
                <p className="text-sm text-[#6B6560] mb-2">
                  Vi svarar inom 24 timmar.
                </p>
                <a href="mailto:hej@operio.nu"
                   className="text-sm text-[#E8613A] font-medium hover:underline"
                   data-testid="policy-contact-email">
                  hej@operio.nu →
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E8E2D9] bg-white/50">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/operio-logo-2.png" alt="Operio" className="h-6 w-auto" />
            <span className="text-sm text-[#6B6560]">© {new Date().getFullYear()} Operio AB</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#6B6560]">
            <Link to="/kontakt" className="hover:text-[#1A1A1A]">Kontakt</Link>
            <Link to="/about" className="hover:text-[#1A1A1A]">Om oss</Link>
            <Link to="/integritetspolicy" className="text-[#1A1A1A] font-medium">Integritetspolicy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IntegritetspolicyPage;
